import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import DeliveryInfo from "@/components/DeliveryInfo";
import {
  getDeliveryCharge,
  getDeliveryLabel,
  loadDeliveryPincode,
  normalizePincode,
  saveDeliveryPincode,
} from "@/utils/delivery";

export default function CartPage() {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pincode, setPincode] = useState(loadDeliveryPincode);

  const delivery = getDeliveryCharge(pincode);
  const deliveryLabel = getDeliveryLabel(pincode);
  const pincodeReady = pincode.length === 6;
  const grandTotal = cart.total + (delivery ?? 0);

  const handlePincodeChange = (value) => {
    const next = normalizePincode(value);
    setPincode(next);
    saveDeliveryPincode(next);
  };

  if (!user) return (
    <div className="min-h-screen bg-petal-beige flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <ShoppingBag className="w-16 h-16 text-petal-border mx-auto mb-4" />
        <h2 className="font-playfair text-3xl text-petal-olive-text mb-4">Please Login</h2>
        <p className="font-outfit text-sm text-petal-muted mb-8">Sign in to view your cart</p>
        <Link to="/login" className="btn-primary">Login / Register</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-petal-beige" data-testid="cart-page">
      <div className="bg-[#F5F0E8] py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <p className="font-dancing text-petal-gold text-xl mb-2">Your Selections</p>
          <h1 className="font-playfair text-4xl sm:text-5xl text-petal-olive-text">Shopping Cart</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {cart.items.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
            <ShoppingBag className="w-16 h-16 text-petal-border mx-auto mb-6" />
            <h2 className="font-playfair text-3xl text-petal-olive-text mb-4">Your cart is empty</h2>
            <p className="font-outfit text-sm text-petal-muted mb-8">Start adding beautiful gifts to your cart</p>
            <Link to="/shop" className="btn-primary inline-block">Browse Gifts</Link>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              <AnimatePresence>
                {cart.items.map(item => (
                  <motion.div key={item.product_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -50 }}
                    className="bg-white border border-petal-border p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                    <img src={item.product?.images?.[0] || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=200&q=80"}
                      alt={item.product?.name} className="w-20 h-20 object-cover border border-petal-border shrink-0 cursor-pointer"
                      onClick={() => navigate(`/shop/${item.product_id}`)} />
                    <div className="flex-1 min-w-0">
                      <p className="font-outfit text-xs text-petal-muted uppercase tracking-widest">{item.product?.category}</p>
                      <h3 className="font-playfair text-base text-petal-olive-text line-clamp-1 cursor-pointer hover:text-petal-olive"
                        onClick={() => navigate(`/shop/${item.product_id}`)}>{item.product?.name}</h3>
                      {item.custom_note && (
                        <p className="font-outfit text-xs text-petal-muted italic mt-1 line-clamp-1">"{item.custom_note}"</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-petal-border">
                          <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} data-testid={`decrease-qty-${item.product_id}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-petal-beige-alt transition-colors">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center font-outfit text-sm">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} data-testid={`increase-qty-${item.product_id}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-petal-beige-alt transition-colors">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-outfit text-base font-semibold text-petal-olive-text">
                            ₹{item.item_total?.toLocaleString()}
                          </span>
                          <button onClick={() => removeFromCart(item.product_id)} data-testid={`remove-${item.product_id}`}
                            className="text-petal-muted hover:text-red-400 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <div>
              <div className="bg-white border border-petal-border p-6 sticky top-24" data-testid="order-summary">
                <h2 className="font-playfair text-xl text-petal-olive-text mb-6">Order Summary</h2>

                <div className="mb-6">
                  <label className="block font-outfit text-xs uppercase tracking-widest text-petal-olive-text mb-1.5">
                    Delivery Pincode
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    placeholder="Enter 6-digit pincode"
                    data-testid="cart-pincode"
                    className="w-full px-3 py-2.5 border border-petal-border bg-petal-beige font-outfit text-sm focus:outline-none focus:border-petal-olive transition-colors"
                  />
                  {!pincodeReady && (
                    <p className="font-outfit text-xs text-petal-muted mt-1.5">Enter pincode to calculate delivery</p>
                  )}
                  {pincodeReady && deliveryLabel && (
                    <p className="font-outfit text-xs text-petal-olive mt-1.5">{deliveryLabel}</p>
                  )}
                </div>

                <DeliveryInfo compact />

                <div className="space-y-3 mb-6 mt-6">
                  <div className="flex justify-between font-outfit text-sm">
                    <span className="text-petal-muted">Subtotal ({cart.item_count} items)</span>
                    <span className="text-petal-olive-text font-medium">₹{cart.total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-outfit text-sm">
                    <span className="text-petal-muted">Delivery</span>
                    <span className="text-petal-olive-text font-medium">
                      {pincodeReady ? `₹${delivery}` : "—"}
                    </span>
                  </div>
                  <div className="border-t border-petal-border pt-3 flex justify-between">
                    <span className="font-outfit text-sm font-semibold text-petal-olive-text">Total</span>
                    <span className="font-playfair text-xl text-petal-olive-text font-semibold" data-testid="cart-total">
                      ₹{(pincodeReady ? grandTotal : cart.total).toLocaleString()}
                      {!pincodeReady && <span className="font-outfit text-xs text-petal-muted font-normal"> + delivery</span>}
                    </span>
                  </div>
                </div>
                <button onClick={() => navigate("/checkout")} data-testid="proceed-checkout-btn"
                  className="w-full btn-primary flex items-center justify-center gap-2 py-4">
                  Proceed to Checkout <ArrowRight className="w-4 h-4" />
                </button>
                <Link to="/shop" className="block text-center font-outfit text-xs text-petal-muted uppercase tracking-widest mt-4 hover:text-petal-olive transition-colors">
                  Continue Shopping
                </Link>

                {/* Benefits */}
                <div className="mt-6 border-t border-petal-border pt-6 space-y-3">
                  {["Handcrafted with love", "Secure checkout", "Same-day delivery available"].map(b => (
                    <div key={b} className="flex items-center gap-2 font-outfit text-xs text-petal-muted">
                      <div className="w-1.5 h-1.5 bg-petal-gold rounded-full" />
                      {b}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
